<template>
  <div id="app">
    <div v-if="realtimeEnabled && !!auth">
      <PulsRealtime
        :auth="auth"
        :app="'web'"
        :debug="debugRealtime"
        :environment="environment"
        :messageReceivedCallback="realtimeMessageReceived"
        :connectedCallback="realtimeConnectedCallback"
        :disconnectedCallback="realtimeDisconnectedCallback"
        class="ms-3 me-3"
      />
    </div>
    <PulsBackground background-variant="8">
      <ThemeSelector />

      <PulsNavigationMenu
        v-if="isLoggedIn"
        :current-item="currentMenuItem"
        :custom-menu="customMenu"
        :show-logo="true"
        :idToken="idToken"
        :available-locales="availableLocales"
        :current-locale="$i18n.locale"
        :on-locale-change="changeLanguage"
        :on-logout="logout"
        :on-role-select="handleRoleSelect"
        :router="$router"
      />
      <router-view />
    </PulsBackground>
  </div>
</template>

<script>
import { PulsRealtime } from '@puls-solutions/puls-realtime-vue'
import { PulsNavigationMenu } from '@puls-solutions/pulsmeter-menu'
import DevOps from '@puls-solutions/puls-devops-utils'
import {
  PulsBackground,
  ThemeSelector
} from '@puls-solutions/puls-ui-components'
import Amplify from 'aws-amplify'
import { logout as amplifyLogout } from './services/AwsAmplifyService'
export default {
  name: 'app',
  components: {
    PulsBackground,
    ThemeSelector,
    PulsRealtime,
    PulsNavigationMenu
  },
  data() {
    return {
      realtimeConnectionId: null,
      customMenu: null,
      localesMap: {
        ar: 'ar-SA',
        da: 'da-DK',
        de: 'de-DE',
        en: 'en-US',
        es: 'es-ES',
        et: 'et-EE',
        fi: 'fi-FI',
        fr: 'fr-FR',
        hr: 'hr-HR',
        it: 'it-IT',
        ms: 'ms-MY',
        nl: 'nl-NL',
        no: 'no-NO',
        pl: 'pl-PL',
        ru: 'ru-RU',
        sl: 'sl-SI',
        so: 'so-SO',
        sv: 'sv-SE',
        sr: 'sr-RS',
        uk: 'uk-UA',
        zh: 'zh-CN'
      }
    }
  },
  mounted() {
    if (
      '-ms-scroll-limit' in document.documentElement.style &&
      '-ms-ime-align' in document.documentElement.style
    ) {
      // detect it's IE11
      const handleIERouting = () => {
        var currentPath = window.location.hash.slice(1)
        if (this.$route.path !== currentPath) {
          this.$router.push(currentPath)
        }
      }
      window.removeEventListener('hashchange', handleIERouting)
      window.addEventListener('hashchange', handleIERouting, false)
    }

    if (localStorage.orgData) {
      const o = JSON.parse(localStorage.orgData)
      if (o.orgSettings?.customMenu) {
        this.customMenu = o.orgSettings.customMenu
      }
    }
  },
  computed: {
    availableLocales() {
      if (localStorage.orgData) {
        const orgData = JSON.parse(localStorage.orgData)
        if (orgData.orgSettings?.locales) {
          return Object.keys(orgData.orgSettings.locales)
        }
      }
      return this.$i18n.availableLocales
    },
    idToken() {
      const user = this.$store.getters.loggedInUser
      return user?.signInUserSession?.idToken?.payload || {}
    },
    currentMenuItem() {
      return 'overview'
    },
    realtimeEnabled() {
      return this.environment === 'dev'
    },
    environment() {
      return DevOps.getEnvironment({})
    },
    auth() {
      const user = this.$store.getters.loggedInUser
      return user?.signInUserSession?.idToken?.jwtToken
    },
    userIsAdmin() {
      return this.$store.getters.isAdmin
    },
    isLoggedIn() {
      return this.$store.getters.loggedInUser && this.$store.getters.hasToken
    },
    username() {
      return this.$store.getters.userInfo.fullName
    },
    debugRealtime() {
      // check if query params includes 'debug'
      return this.$route.query.debug === 'true'
    }
  },
  methods: {
    changeLanguage(langCode) {
      window.localStorage.setItem('selectedLocale', langCode)
      this.$i18n.locale = langCode
      const bcp47Locale = this.localesMap[langCode]
      if (bcp47Locale) {
        Amplify.API.put('UserAPI', '/user', {
          body: { locale: bcp47Locale }
        })
      }
    },
    logout() {
      return amplifyLogout()
        .then(() => this.afterLogout())
        .catch(() => this.afterLogout())
    },
    afterLogout() {
      this.$store.commit('setLoggedInUser', null)
      this.$store.commit('setHasToken', false)
      window.localStorage.removeItem('orgData')
      this.$router.push('/loggedout')
    },
    handleRoleSelect() {
      // PulsNavigationMenu handles role persistence and navigation internally.
      // This handler is kept as a no-op for future extensibility.
    },
    realtimeMessageReceived(msg) {
      if (msg.action === 'reload') {
        window.location.reload()
      }
      if (msg.action === 'alert') {
        alert(msg.message)
      }
      if (msg.action === 'logout') {
        window.location.href = '/#/logout'
      }
    },
    realtimeConnectedCallback(msg) {
      this.realtimeConnectionId = msg.connectionId
    },
    realtimeDisconnectedCallback(msg) {
      this.realtimeConnectionId = null
    },
    onClickNavItem(item) {
      if (item.link) {
        window.location = item.link
      } else {
        this.$router.push(item)
      }
    }
  }
}
</script>
